import { Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { setScoutingPin } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { List, X } from "@phosphor-icons/react";
import { Brand, NavMenu, UserFooter } from "@/components/layout/Sidebar";
import SupportWidget from "@/components/SupportWidget";
import WhatsNewDialog from "@/components/WhatsNewDialog";
import TutorialLibrary from "@/components/TutorialLibrary";
import TrialBanner from "@/components/TrialBanner";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import LanguageToggle from "@/components/LanguageToggle";
import { RosterStatusProvider } from "@/context/RosterStatusContext";
import { BodyMapProvider } from "@/context/BodyMapContext";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Heartbeat: mark the user active + report the current area, so admins see who's online, from where, and time per area.
  useEffect(() => {
    if (!user) return;
    const areaFromPath = (p) => {
      if (p.startsWith("/admin")) return "Admin";
      if (p.startsWith("/direzione-sportiva")) return "Sport Director";
      if (p.startsWith("/analysis/medical")) return "Medical Health";
      if (p.startsWith("/analysis")) return "Analysis";
      if (["/da-vedere", "/calciatrici", "/da-ingaggiare", "/campionati", "/paesi", "/squad", "/confronto"].some((x) => p.startsWith(x))) return "Scouting";
      if (p === "/") return "Dashboard";
      return "Other";
    };
    const ping = () => { if (document.visibilityState === "visible") api.post("/activity/ping", { area: areaFromPath(window.location.pathname) }).catch(() => {}); };
    ping();
    const id = setInterval(ping, 60000);
    document.addEventListener("visibilitychange", ping);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", ping); };
  }, [user]);

  const doLogout = async () => {
    setScoutingPin("");
    await logout();
    navigate("/login", { replace: true });
  };

  const sidebar = (
    <div className="flex flex-col h-full">
      <Brand />
      <NavMenu onNavigate={() => setOpen(false)} />
      <UserFooter email={user?.email} onLogout={doLogout} />
    </div>
  );

  return (
    <RosterStatusProvider>
    <BodyMapProvider>
    <div className="min-h-screen flex bg-background" style={{ backgroundImage: "linear-gradient(var(--bg-ov1, rgba(10,15,28,0.965)), var(--bg-ov2, rgba(10,15,28,0.985))), var(--app-texture-url, url(/assets/texture_dark.jpg))", backgroundSize: "var(--app-texture-size, 480px)", backgroundAttachment: "fixed" }}>
      <aside className="hidden lg:block w-64 shrink-0 border-r border-border fixed inset-y-0 left-0" style={{ backgroundImage: "linear-gradient(var(--bg-ov-aside1, rgba(9,14,26,0.90)), var(--bg-ov-aside2, rgba(9,14,26,0.95))), var(--app-texture-url, url(/assets/texture_dark.jpg))", backgroundSize: "var(--app-texture-aside-size, 300px)" }}>{sidebar}</aside>

      <div className="lg:hidden fixed top-0 inset-x-0 z-30 flex items-center justify-between px-4 h-14 border-b border-border bg-background">
        <Brand small />
        <div className="flex items-center gap-2">
          <LanguageToggle />
          <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}>
            {open ? <X size={22} /> : <List size={22} />}
          </Button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-20 pt-14">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-14 bottom-0 w-72 bg-background border-r border-border">{sidebar}</aside>
        </div>
      )}

      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0 min-w-0">
        <ImpersonationBanner />
        <TrialBanner />
        <WhatsNewDialog />
        <div className="p-6 lg:p-10 w-full 2xl:max-w-[2000px]">
          <Outlet />
        </div>
      </main>
      <div className="fixed bottom-5 right-5 z-40 flex items-center gap-2">
        <TutorialLibrary triggerClassName="rounded-full bg-card border border-border text-foreground shadow-lg px-4 py-3 hover:border-primary" triggerLabel={<span className="hidden sm:inline">Guide</span>} />
        <SupportWidget triggerClassName="rounded-full bg-primary text-primary-foreground shadow-lg px-4 py-3 hover:brightness-110" triggerLabel={<span className="hidden sm:inline">Aiuto</span>} />
      </div>
    </div>
    </BodyMapProvider>
    </RosterStatusProvider>
  );
}
